import type {
  WatchAiSuggestion,
  WatchBuilding,
  WatchCompany,
  WatchInvestigationCollection,
  WatchInvestigationFieldChange,
  WatchProduct,
  WatchTowerMockState,
} from './types';

export const COMPANY_INVESTIGATION_DELAY_MS = 1400;

export function enqueueCompanyInvestigation(
  state: WatchTowerMockState,
  companyIds: number[],
): WatchTowerMockState {
  const ids = new Set(companyIds.filter(Boolean));
  if (!ids.size) {
    return state;
  }
  return {
    ...state,
    watchCompanies: state.watchCompanies.map((company) => (
      ids.has(company.id)
        ? {
          ...company,
          aiStatus: 'review_ongoing' as const,
          aiErrorMessage: null,
          aiSuggestion: null,
        }
        : company
    )),
  };
}

export function fieldChange(
  suggestion: WatchAiSuggestion | null,
  path: string,
): WatchInvestigationFieldChange | null {
  return suggestion?.fieldChanges.find((item) => item.path === path) || null;
}

export type WatchDossierRowKeySource = {
  id?: number,
  uid?: string | null,
  name?: string | null,
  code?: string | null,
  personFullName?: string | null,
  eventKind?: string | null,
  periodEnd?: string | null,
};

export function rowKey(item: WatchDossierRowKeySource): string {
  if (item.uid) {
    return item.uid;
  }
  if (item.code) {
    return item.code;
  }
  if (item.name) {
    return item.name;
  }
  if (item.personFullName) {
    return item.personFullName;
  }
  if (item.eventKind && item.periodEnd) {
    return `${item.eventKind}:${item.periodEnd}`;
  }
  if (item.periodEnd) {
    return item.periodEnd;
  }
  if (typeof item.id === 'number' && item.id > 0) {
    return `id:${item.id}`;
  }
  return '';
}

export function reviewItemKeys(item: WatchDossierRowKeySource): string[] {
  const keys: string[] = [];
  if (typeof item.id === 'number' && item.id > 0) {
    keys.push(`id:${item.id}`);
  }
  const fallback = rowKey(item);
  if (fallback && !keys.includes(fallback)) {
    keys.push(fallback);
  }
  return keys;
}

export interface WatchReviewRow<T> {
  key: string,
  op: 'same' | 'add' | 'remove' | 'update',
  item: T,
  patchedFields?: string[],
  reason?: string,
}

export function currentCollectionRows<T extends WatchDossierRowKeySource>(
  items: T[],
): WatchReviewRow<T>[] {
  return items.map((item) => ({
    key: reviewItemKeys(item)[0] || rowKey(item),
    op: 'same' as const,
    item,
  }));
}

export function reviewCollectionRows<T extends WatchDossierRowKeySource>(
  items: T[],
  suggestion: WatchAiSuggestion | null,
  collection: WatchInvestigationCollection,
): WatchReviewRow<T>[] {
  const changes = (suggestion?.rowChanges || [])
    .filter((item) => item.collection === collection);
  const rows: WatchReviewRow<T>[] = items.map((item) => {
    const keys = reviewItemKeys(item);
    const key = keys[0] || rowKey(item);
    const remove = changes.find((change) => change.op === 'remove' && keys.includes(change.key));
    if (remove) {
      return {
        key, op: 'remove' as const, item, reason: remove.reason,
      };
    }
    const update = changes.find((change) => change.op === 'update' && keys.includes(change.key));
    if (update?.after) {
      return {
        key,
        op: 'update' as const,
        item: { ...item, ...update.after } as T,
        patchedFields: update.patchedFields,
        reason: update.reason,
      };
    }
    return { key, op: 'same' as const, item };
  });
  changes.filter((item) => item.op === 'add').forEach((change, index) => {
    rows.push({
      key: change.key,
      op: 'add',
      item: { id: -1 - index, ...(change.after || {}) } as T,
    });
  });
  return rows;
}

function nextChildId(items: { id: number }[]): number {
  return items.reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

function asText(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

function productFromPatch(raw: Record<string, unknown>, id: number): WatchProduct {
  return {
    id,
    name: asText(raw.name),
    code: typeof raw.code === 'string' ? raw.code : null,
    uid: asText(raw.uid) || `WT-P-${id}`,
    productCategory: typeof raw.productCategory === 'string' ? raw.productCategory : null,
    productType: typeof raw.productType === 'string' ? raw.productType : null,
    upstreamTier: typeof raw.upstreamTier === 'string' ? raw.upstreamTier : null,
    dateOpened: typeof raw.dateOpened === 'string' ? raw.dateOpened : null,
    dateClosed: typeof raw.dateClosed === 'string' ? raw.dateClosed : null,
    volumeQty: typeof raw.volumeQty === 'number' ? raw.volumeQty : null,
    volumeQtyUnit: typeof raw.volumeQtyUnit === 'string' ? raw.volumeQtyUnit : null,
    relianceOnFreeNaturalResources: [],
    relianceOnFreeSocialResources: [],
    criticalityRating: typeof raw.criticalityRating === 'string' || typeof raw.criticalityRating === 'number'
      ? raw.criticalityRating
      : null,
    circularityFromDownstreamWaste: null,
    hsCode: typeof raw.hsCode === 'string' ? raw.hsCode : null,
    description: typeof raw.description === 'string' ? raw.description : null,
  };
}

function buildingFromPatch(raw: Record<string, unknown>, id: number): WatchBuilding {
  return {
    id,
    name: asText(raw.name),
    uid: asText(raw.uid) || `WT-B-${id}`,
    legalName: typeof raw.legalName === 'string' ? raw.legalName : null,
    ownership: typeof raw.ownership === 'string' ? raw.ownership : null,
    equityShareholding: typeof raw.equityShareholding === 'number' ? raw.equityShareholding : null,
    financialControl: typeof raw.financialControl === 'number' ? raw.financialControl : null,
    businessUnitType: typeof raw.businessUnitType === 'string' ? raw.businessUnitType : null,
    assetCategory: Array.isArray(raw.assetCategory)
      ? raw.assetCategory.filter((item): item is string => typeof item === 'string')
      : [],
    ownedLeased: typeof raw.ownedLeased === 'string' ? raw.ownedLeased : null,
    numberOfEmployees: typeof raw.numberOfEmployees === 'number' ? raw.numberOfEmployees : null,
    dateOpened: typeof raw.dateOpened === 'string' ? raw.dateOpened : null,
    dateClosed: typeof raw.dateClosed === 'string' ? raw.dateClosed : null,
    startDate: typeof raw.startDate === 'string' ? raw.startDate : null,
    endDate: typeof raw.endDate === 'string' ? raw.endDate : null,
    naceSection: typeof raw.naceSection === 'string' ? raw.naceSection : null,
    naceCode: typeof raw.naceCode === 'string' ? raw.naceCode : null,
    euTaxonomyCode: null,
    netBookAmount: typeof raw.netBookAmount === 'number' ? raw.netBookAmount : null,
    netBookCurrency: typeof raw.netBookCurrency === 'string' ? raw.netBookCurrency : null,
    addressCountry: typeof raw.addressCountry === 'string' ? raw.addressCountry : null,
    addressState: typeof raw.addressState === 'string' ? raw.addressState : null,
    addressPostCode: typeof raw.addressPostCode === 'string' ? raw.addressPostCode : null,
    addressCity: typeof raw.addressCity === 'string' ? raw.addressCity : null,
    addressStreet: typeof raw.addressStreet === 'string' ? raw.addressStreet : null,
    addressBuilding: typeof raw.addressBuilding === 'string' ? raw.addressBuilding : null,
    addressCoordinates: typeof raw.addressCoordinates === 'string' ? raw.addressCoordinates : null,
  };
}

function applyRowChanges(company: WatchCompany, suggestion: WatchAiSuggestion): WatchCompany {
  let products = [...company.products];
  let buildings = [...company.buildings];
  suggestion.rowChanges.forEach((change) => {
    if (change.collection === 'products') {
      if (change.op === 'remove') {
        products = products.filter((item) => rowKey(item) !== change.key);
      }
      if (change.op === 'add' && change.after) {
        products = [...products, productFromPatch(change.after, nextChildId(products))];
      }
      if (change.op === 'update' && change.after) {
        products = products.map((item) => (
          rowKey(item) === change.key ? { ...item, ...productFromPatch(change.after || {}, item.id) } : item
        ));
      }
    }
    if (change.collection === 'buildings') {
      if (change.op === 'remove') {
        buildings = buildings.filter((item) => rowKey(item) !== change.key);
      }
      if (change.op === 'add' && change.after) {
        buildings = [...buildings, buildingFromPatch(change.after, nextChildId(buildings))];
      }
      if (change.op === 'update' && change.after) {
        buildings = buildings.map((item) => (
          rowKey(item) === change.key ? { ...item, ...buildingFromPatch(change.after || {}, item.id) } : item
        ));
      }
    }
  });
  return { ...company, products, buildings };
}

function applyFieldChanges(company: WatchCompany, suggestion: WatchAiSuggestion): WatchCompany {
  const next: WatchCompany = { ...company };
  suggestion.fieldChanges.forEach((change) => {
    if (change.path === 'aliases') {
      next.aliases = change.after.split(',').map((item) => item.trim()).filter(Boolean);
      return;
    }
    if (change.path === 'name') {
      next.name = change.after || next.name;
      return;
    }
    if (change.path === 'code') {
      next.code = change.after || null;
      return;
    }
    if (change.path === 'website') {
      next.website = change.after || null;
      return;
    }
    if (change.path === 'notes') {
      next.notes = change.after || null;
      return;
    }
    if (change.path === 'addressCity') {
      next.addressCity = change.after || null;
      return;
    }
    if (change.path === 'naceSection') {
      next.naceSection = change.after || null;
      return;
    }
    if (change.path === 'naceCode') {
      next.naceCode = change.after || null;
    }
  });
  return next;
}

export function applyCompanyInvestigation(
  state: WatchTowerMockState,
  companyId: number,
): WatchTowerMockState {
  const company = state.watchCompanies.find((item) => item.id === companyId);
  if (!company?.aiSuggestion || company.aiStatus !== 'awaiting_human_review') {
    return state;
  }
  const patched = applyRowChanges(
    applyFieldChanges(company, company.aiSuggestion),
    company.aiSuggestion,
  );
  return {
    ...state,
    watchCompanies: state.watchCompanies.map((item) => (
      item.id === companyId
        ? {
          ...patched,
          aiStatus: 'human_accepted' as const,
          aiErrorMessage: null,
          aiSuggestion: null,
        }
        : item
    )),
  };
}

export function rejectCompanyInvestigation(
  state: WatchTowerMockState,
  companyId: number,
): WatchTowerMockState {
  return {
    ...state,
    watchCompanies: state.watchCompanies.map((item) => (
      item.id === companyId
        && (item.aiStatus === 'awaiting_human_review' || item.aiStatus === 'in_error')
        ? {
          ...item,
          aiStatus: 'human_rejected' as const,
          aiErrorMessage: null,
          aiSuggestion: null,
        }
        : item
    )),
  };
}

export function retryCompanyInvestigation(
  state: WatchTowerMockState,
  companyId: number,
): WatchTowerMockState {
  const company = state.watchCompanies.find((item) => item.id === companyId);
  if (!company || company.aiStatus !== 'in_error') {
    return state;
  }
  return enqueueCompanyInvestigation(state, [companyId]);
}

function cannedSuggestion(company: WatchCompany): WatchAiSuggestion | null {
  if (company.uid === 'WT-POVALLEY-001') {
    return {
      fieldChanges: [
        {
          path: 'website',
          before: company.website || '',
          after: 'https://www.povalleycarton.example',
        },
        {
          path: 'notes',
          before: company.notes || '',
          after: 'Folding carton for food brands. AI: also luxury packaging.',
        },
      ],
      rowChanges: [
        {
          collection: 'products',
          op: 'add',
          key: 'WT-POVALLEY-P-BEV-010',
          before: null,
          after: {
            name: 'Beverage carton board',
            code: 'PVC-BEV-010',
            uid: 'WT-POVALLEY-P-BEV-010',
            productCategory: 'Carton board',
            productType: 'output',
            hsCode: '4819.10',
            description: 'Board for beverage sleeves',
          },
        },
        {
          collection: 'buildings',
          op: 'add',
          key: 'WT-POVALLEY-B-31',
          before: null,
          after: {
            name: 'Modena mill',
            uid: 'WT-POVALLEY-B-31',
            addressCountry: 'IT',
            addressCity: 'Modena',
            ownedLeased: 'Owned',
            assetCategory: ['Factory'],
          },
        },
      ],
    };
  }
  if (company.uid === 'WT-HELIOS-001') {
    return {
      fieldChanges: [
        {
          path: 'website',
          before: company.website || '',
          after: 'https://www.helios-polymers.example/investors',
        },
      ],
      rowChanges: [
        {
          collection: 'products',
          op: 'add',
          key: 'WT-HELIOS-P-REC-01',
          before: null,
          after: {
            name: 'HelioRecycle PET flake',
            code: 'HF-REC-01',
            uid: 'WT-HELIOS-P-REC-01',
            productCategory: 'Polymer resin',
            productType: 'input',
            hsCode: '3907.61',
            description: 'Recycled PET flake for food-contact film',
          },
        },
      ],
    };
  }
  return null;
}

function suggestionIsEmpty(suggestion: WatchAiSuggestion | null): boolean {
  if (!suggestion) {
    return true;
  }
  return suggestion.fieldChanges.length === 0 && suggestion.rowChanges.length === 0;
}

export function completeCompanyInvestigations(
  state: WatchTowerMockState,
  companyIds: number[],
  options: { errorIds?: number[] } = {},
): WatchTowerMockState {
  const ids = new Set(companyIds);
  const errorIds = new Set(options.errorIds || []);
  return {
    ...state,
    watchCompanies: state.watchCompanies.map((company) => {
      if (!ids.has(company.id) || company.aiStatus !== 'review_ongoing') {
        return company;
      }
      if (errorIds.has(company.id)) {
        return {
          ...company,
          aiStatus: 'in_error' as const,
          aiErrorMessage: 'Company Investigation failed. Retry the request.',
          aiSuggestion: null,
        };
      }
      const suggestion = cannedSuggestion(company);
      if (suggestionIsEmpty(suggestion)) {
        return {
          ...company,
          aiStatus: 'finalized' as const,
          aiErrorMessage: null,
          aiSuggestion: null,
        };
      }
      return {
        ...company,
        aiStatus: 'awaiting_human_review' as const,
        aiErrorMessage: null,
        aiSuggestion: suggestion,
      };
    }),
  };
}

export function pendingInvestigationIds(state: WatchTowerMockState): number[] {
  return state.watchCompanies
    .filter((company) => company.aiStatus === 'review_ongoing')
    .map((company) => company.id);
}
